/**
 * Sync Helpers - Compartido entre Web (Dexie) y Mobile (SQLite)
 *
 * Funciones helper para emitir eventos de cambio en repositorios.
 * Usadas por ambos adapters para mantener consistencia.
 *
 * Uso:
 *   import { emitInsert, emitUpdate, emitDelete } from './syncHelpers';
 *   // o desde mobile:
 *   import { emitInsert, emitUpdate, emitDelete } from '@shared/services/storage/syncHelpers';
 */

import { repositoryEvents, ChangeAction, RepositoryChangeEvent, localRepositoryEvents } from './RepositoryChangeEventEmitter';

/**
 * Mapeo de tablas locales a tablas Supabase
 * Algunas tablas locales no se sincronizan (profiles, etc.)
 *
 * NOTA: 'animales' NO se sincroniza directamente porque es una tabla derivada/caché local.
 * Solo 'animales_historico' se sincroniza con Supabase.
 * La tabla 'animales' se regenera automáticamente vía el trigger 'sync_animales' en Supabase
 * cuando se insertan registros en 'animales_historico'.
 *
 * NOTA: 'stocks' NO se sincroniza porque es una tabla local de stock actual.
 * Solo 'stocks_historico' se sincroniza con Supabase.
 */
const TABLE_MAPPING: Record<string, string | null> = {
  'animales': null,  // No sincronizar directamente - se regenera vía trigger desde animales_historico
  'animales_historico': 'animales_historico',
  'ubicaciones': 'ubicaciones',
  'unidades': 'unidades',
  'empresas': 'empresas',
  'operacion_compra_venta_animales': 'operacion_compra_venta_animales',  // tabla local → tabla Supabase
  'gastos': 'gastos',
  'ingresos': 'ingresos',
  'stocks': null,  // No sincronizar - tabla local de stock actual (solo stocks_historico se sincroniza)
  'stocks_historico': 'stocks_historico',
  'notas': 'notas',
  // Notas ancladas (spec 2026-10-16): anclas + personal del campo.
  'nota_anclas': 'nota_anclas',
  'personas_campo': 'personas_campo',
  'eventos': 'eventos',
  'recordatorios': 'recordatorios',
  'lluvias': 'lluvias',
  'elementos_mapa': 'elementos_mapa',
  // Puntos de control del mapa (puntos + observaciones históricas)
  'puntos_control': 'puntos_control',
  'puntos_control_eventos': 'puntos_control_eventos',
  // Lotes de siembra: siembras de cada lote (elementos_mapa tipo SIEMBRA) con su historial
  'siembras_lote': 'siembras_lote',
  // Taxonomía de conceptos (spec 2026-08-07): catálogo + tablas de relación
  // por empresa (ocultos/confirmados), todas sincronizan como filas normales.
  'categorias': 'categorias',
  'conceptos': 'conceptos',
  'conceptos_ocultos': 'conceptos_ocultos',
  'conceptos_confirmados': 'conceptos_confirmados',
  // Tablas que NO se sincronizan
  'profiles': null,
  'categorias_cache': null,
};

/**
 * Tablas que deben sincronizarse
 */
export const SYNCABLE_TABLES = Object.entries(TABLE_MAPPING)
  .filter(([, supabaseTable]) => supabaseTable !== null)
  .map(([localTable]) => localTable);

/**
 * Emite un evento de inserción
 *
 * NOTA: Emite eventos locales para tablas que no se sincronizan (como 'animales')
 * para que los componentes puedan reaccionar a cambios locales (offline-first).
 */
export function emitInsert(localTable: string, record: Record<string, unknown>): void {
  const supabaseTable = TABLE_MAPPING[localTable];

  // Si la tabla se sincroniza, emitir con el nombre de la tabla Supabase
  if (supabaseTable) {
    repositoryEvents.emit(supabaseTable, 'INSERT', record);
  } else {
    // Si no se sincroniza, emitir con el nombre de la tabla local
    // Esto permite que los componentes reaccionen a cambios locales (offline-first)
    repositoryEvents.emit(localTable, 'INSERT', record);
  }
}

/**
 * Emite un evento de actualización
 *
 * NOTA: Emite eventos locales para tablas que no se sincronizan (como 'animales')
 * para que los componentes puedan reaccionar a cambios locales (offline-first).
 */
export function emitUpdate(localTable: string, record: Record<string, unknown>): void {
  const supabaseTable = TABLE_MAPPING[localTable];

  // Si la tabla se sincroniza, emitir con el nombre de la tabla Supabase
  if (supabaseTable) {
    repositoryEvents.emit(supabaseTable, 'UPDATE', record);
  } else {
    // Si no se sincroniza, emitir con el nombre de la tabla local
    // Esto permite que los componentes reaccionen a cambios locales (offline-first)
    repositoryEvents.emit(localTable, 'UPDATE', record);
  }
}

/**
 * Emite un evento de eliminación
 *
 * NOTA: Emite eventos locales para tablas que no se sincronizan (como 'animales')
 * para que los componentes puedan reaccionar a cambios locales (offline-first).
 */
export function emitDelete(localTable: string, id: string): void {
  const supabaseTable = TABLE_MAPPING[localTable];

  // Si la tabla se sincroniza, emitir con el nombre de la tabla Supabase
  if (supabaseTable) {
    repositoryEvents.emit(supabaseTable, 'DELETE', id);
  } else {
    // Si no se sincroniza, emitir con el nombre de la tabla local
    // Esto permite que los componentes reaccionen a cambios locales (offline-first)
    repositoryEvents.emit(localTable, 'DELETE', id);
  }
}

/**
 * Emite un evento de soft delete (se trata como UPDATE)
 */
export function emitSoftDelete(localTable: string, record: Record<string, unknown>): void {
  emitUpdate(localTable, record);
}

/**
 * Emite un evento de inserción BULK (array de registros en una sola operación de Supabase)
 *
 * Solo se emite si la tabla se sincroniza con Supabase. Para tablas no sincronizables
 * (como 'animales') no se emite nada porque no hay destino en Supabase.
 */
export function emitBulkInsert(localTable: string, records: Record<string, unknown>[]): void {
  const supabaseTable = TABLE_MAPPING[localTable];
  if (!supabaseTable || records.length === 0) return;

  repositoryEvents.emit(supabaseTable, 'BULK_INSERT', records);
}

/**
 * Emite un evento de eliminación BULK (array de IDs en una sola operación de Supabase)
 */
export function emitBulkDelete(localTable: string, ids: string[]): void {
  const supabaseTable = TABLE_MAPPING[localTable];
  if (!supabaseTable || ids.length === 0) return;

  repositoryEvents.emit(supabaseTable, 'BULK_DELETE', ids);
}

/**
 * Emite un evento de actualización BULK (array de registros parciales).
 *
 * SyncManager lo resuelve con un único upsert + merge (onConflict=pk,
 * defaultToNull:false) para que solo se actualicen las columnas enviadas.
 * Se emite un ÚNICO evento (y un único pending change offline) en lugar de
 * N eventos UPDATE.
 */
export function emitBulkUpdate(localTable: string, records: Record<string, unknown>[]): void {
  const supabaseTable = TABLE_MAPPING[localTable];
  if (!supabaseTable || records.length === 0) return;

  repositoryEvents.emit(supabaseTable, 'BULK_UPDATE', records);
}

/**
 * Emite un evento de restauración (se trata como UPDATE)
 */
export function emitRestore(localTable: string, record: Record<string, unknown>): void {
  emitUpdate(localTable, record);
}

/**
 * Helper para emitir eventos desde repositorios
 * Combina la emisión con el registro de la operación
 */
export function emitRepositoryChange(
  localTable: string,
  action: ChangeAction,
  recordOrId: Record<string, unknown> | string
): void {
  const supabaseTable = TABLE_MAPPING[localTable];
  if (!supabaseTable) return;

  repositoryEvents.emit(supabaseTable, action, recordOrId);
}

/**
 * Obtiene la tabla de Supabase correspondiente a una tabla local
 */
export function getSupabaseTable(localTable: string): string | null {
  return TABLE_MAPPING[localTable] || null;
}

/**
 * Verifica si una tabla debe sincronizarse
 */
export function isSyncableTable(localTable: string): boolean {
  return TABLE_MAPPING[localTable] !== null && TABLE_MAPPING[localTable] !== undefined;
}

/**
 * Suscribe a eventos de cambio de repositorio
 */
export function subscribeToRepositoryChanges(
  listener: (event: RepositoryChangeEvent) => void
): () => void {
  return repositoryEvents.subscribe(listener);
}

/**
 * ============================================================================
 * EMIT LOCALES - Solo para actualizar componentes UI, NO sincronizan con Supabase
 * ============================================================================
 *
 * Usar estas funciones cuando quieras que los componentes se actualicen
 * pero NO quieres que los datos se sincronicen con Supabase.
 *
 * Ejemplo de uso:
 * - Cargar datos desde Supabase (evitar bucle de sincronización)
 * - Operaciones internas que no deben sincronizarse
 */

/**
 * Emite un evento de inserción LOCAL (no sincroniza con Supabase)
 *
 * Usa localRepositoryEvents que NO es escuchado por SyncManager,
 * por lo que los datos no se sincronizan con Supabase.
 */
export function emitLocalInsert(localTable: string, record: Record<string, unknown>): void {
  localRepositoryEvents.emit(localTable, 'INSERT', record);
}

/**
 * Emite un evento de actualización LOCAL (no sincroniza con Supabase)
 *
 * Usa localRepositoryEvents que NO es escuchado por SyncManager,
 * por lo que los datos no se sincronizan con Supabase.
 */
export function emitLocalUpdate(localTable: string, record: Record<string, unknown>): void {
  localRepositoryEvents.emit(localTable, 'UPDATE', record);
}

/**
 * Emite un evento de eliminación LOCAL (no sincroniza con Supabase)
 *
 * Usa localRepositoryEvents que NO es escuchado por SyncManager,
 * por lo que los datos no se sincronizan con Supabase.
 */
export function emitLocalDelete(localTable: string, id: string): void {
  localRepositoryEvents.emit(localTable, 'DELETE', id);
}

// Re-exportar los singletons para acceso directo
export { repositoryEvents, localRepositoryEvents };
